const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;

const FRAME_FILE_PREFIX = 'frame';
const FILE_NAME_SEPARATOR = '-';
const TIMECODE_SEPARATOR = ':';

export interface FormatTimecodeOptions {
  /** Frames per second — needed for the `.ff` suffix. */
  fps?: number;
  /** Append the frame index within the second (paused display). */
  showFrames?: boolean;
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

/**
 * Render milliseconds as `m:ss`, or `h:mm:ss` past an hour. With
 * `{ fps, showFrames: true }` the frame within the second is appended as
 * `.ff` — the paused player's clock.
 */
export function formatTimecode(ms: number, opts: FormatTimecodeOptions = {}): string {
  const safeMs = Number.isFinite(ms) && ms > 0 ? ms : 0;
  const totalSeconds = Math.floor(safeMs / MS_PER_SECOND);
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;

  const base = hours > 0 ? `${hours}:${pad2(minutes)}:${pad2(seconds)}` : `${minutes}:${pad2(seconds)}`;
  if (!opts.showFrames || !opts.fps || opts.fps <= 0) {
    return base;
  }
  const msWithinSecond = safeMs - totalSeconds * MS_PER_SECOND;
  const frame = Math.floor((msWithinSecond / MS_PER_SECOND) * opts.fps);
  return `${base}.${pad2(frame)}`;
}

/**
 * `frame-0-08` — a frame's file name without its extension, the shape its
 * upload is named in. A file name can't carry the timecode's `:`. Shared by
 * the upload and the chat's viewer (Download), so the two never drift.
 */
export function frameFileStem(tMs: number): string {
  const timecode = formatTimecode(tMs).replaceAll(TIMECODE_SEPARATOR, FILE_NAME_SEPARATOR);
  return `${FRAME_FILE_PREFIX}${FILE_NAME_SEPARATOR}${timecode}`;
}
